import { createClient } from '@supabase/supabase-js';

const url = import.meta.env.VITE_SUPABASE_URL;
const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

if (!url || !anonKey) {
  // Loud, early failure so nobody ships with missing env.
  console.error('[supabase] Missing VITE_SUPABASE_URL or VITE_SUPABASE_ANON_KEY');
}

// Session persists across tab close/reopen by default (localStorage).
// Tokens auto-refresh. Do NOT override these — logout-on-refresh was a
// regression last time.
export const supabase = createClient(url, anonKey, {
  auth: {
    persistSession: true,
    autoRefreshToken: true,
    detectSessionInUrl: true,     // for the password-reset callback
    storageKey: 'proxera.auth',
  },
  realtime: {
    params: { eventsPerSecond: 10 },
  },
});

// ── getFreshAccessToken ──────────────────────────────────────────────
// Return the current Supabase access_token, refreshing first if the
// stored one is within 60s of expiry (or already past it).
//
// Why this exists: `autoRefreshToken: true` relies on a setInterval
// running inside the page. On iOS WKWebView (native Capacitor build)
// that timer gets throttled or fully suspended while the app is
// backgrounded, so the first call after a wake can fire with an
// expired access_token still in the store. The server rejects it as
// 401 and the user sees "Your session expired. Please sign in again."
// even though their refresh_token is perfectly fine.
//
// Callers should treat `null` as "no session" and fall back to their
// usual "please sign in" message. If a refresh fails (bad network,
// revoked refresh_token), we also return null so the caller can treat
// it the same way.
export async function getFreshAccessToken() {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session) return null;

  const expMs = session.expires_at ? session.expires_at * 1000 : 0;
  const needsRefresh = expMs - Date.now() < 60_000; // <60s or already expired
  if (!needsRefresh) return session.access_token;

  const { data, error } = await supabase.auth.refreshSession();
  if (error || !data?.session) return null;
  return data.session.access_token;
}

// ── forceRefreshAccessToken ──────────────────────────────────────────
// Unconditional refresh, used as a one-shot recovery after a 401. If
// the refresh itself fails, returns null — caller should give up and
// surface the "session expired" message.
export async function forceRefreshAccessToken() {
  const { data, error } = await supabase.auth.refreshSession();
  if (error || !data?.session) return null;
  return data.session.access_token;
}
